import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Package, ShoppingCart, Users, DollarSign, TrendingUp, Eye } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminDashboard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const token = localStorage.getItem('chaleur_admin_token');

  useEffect(() => {
    fetchDashboard();
  }, []);

  const fetchDashboard = async () => {
    try {
      const response = await axios.get(`${API}/admin/dashboard`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setData(response.data);
    } catch (error) {
      console.error('Error fetching dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="h-32 bg-gray-100 animate-pulse rounded" />
          ))}
        </div>
      </div>
    );
  }

  const stats = data?.stats || {};

  return (
    <div className="space-y-8" data-testid="admin-dashboard">
      <div>
        <h1 className="text-2xl font-light mb-2">Dashboard</h1>
        <p className="text-muted-foreground">Welcome back to Chaleur Admin</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card className="rounded-none border-gray-200">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Total Revenue</CardTitle>
            <DollarSign className="w-5 h-5 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-light">₹{(stats.total_revenue || 0).toLocaleString()}</div>
          </CardContent>
        </Card>

        <Card className="rounded-none border-gray-200">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Total Orders</CardTitle>
            <ShoppingCart className="w-5 h-5 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-light">{stats.total_orders || 0}</div>
            <p className="text-xs text-muted-foreground">{stats.pending_orders || 0} pending</p>
          </CardContent>
        </Card>

        <Card className="rounded-none border-gray-200">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Products</CardTitle>
            <Package className="w-5 h-5 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-light">{stats.total_products || 0}</div>
          </CardContent>
        </Card>

        <Card className="rounded-none border-gray-200">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Customers</CardTitle>
            <Users className="w-5 h-5 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-light">{stats.total_customers || 0}</div>
          </CardContent>
        </Card>
      </div>

      {/* Recent Orders */}
      <Card className="rounded-none border-gray-200">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-lg font-medium">Recent Orders</CardTitle>
          <Link to="/admin/orders" className="text-sm text-primary hover:underline">
            View All →
          </Link>
        </CardHeader>
        <CardContent>
          {data?.recent_orders?.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-3 text-sm font-medium text-muted-foreground">Order</th>
                    <th className="text-left py-3 text-sm font-medium text-muted-foreground">Customer</th>
                    <th className="text-left py-3 text-sm font-medium text-muted-foreground">Status</th>
                    <th className="text-left py-3 text-sm font-medium text-muted-foreground">Total</th>
                    <th className="text-left py-3 text-sm font-medium text-muted-foreground">Date</th>
                  </tr>
                </thead>
                <tbody>
                  {data.recent_orders.map(order => (
                    <tr key={order.id} className="border-b border-gray-100">
                      <td className="py-3 text-sm font-medium">{order.order_number}</td>
                      <td className="py-3 text-sm text-muted-foreground">{order.customer_id?.slice(0, 8)}...</td>
                      <td className="py-3">
                        <span className={`px-2 py-1 text-xs ${
                          order.status === 'delivered' ? 'bg-green-100 text-green-800' :
                          order.status === 'shipped' ? 'bg-blue-100 text-blue-800' :
                          order.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {order.status}
                        </span>
                      </td>
                      <td className="py-3 text-sm">₹{order.total?.toLocaleString()}</td>
                      <td className="py-3 text-sm text-muted-foreground">
                        {new Date(order.created_at).toLocaleDateString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-muted-foreground text-center py-8">No orders yet</p>
          )}
        </CardContent>
      </Card>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Link to="/admin/products" className="p-6 bg-white border border-gray-200 hover:border-primary transition-colors">
          <Package className="w-8 h-8 mb-4 text-primary" />
          <h3 className="font-medium mb-1">Add Product</h3>
          <p className="text-sm text-muted-foreground">Create new products and manage inventory</p>
        </Link>
        <Link to="/admin/orders" className="p-6 bg-white border border-gray-200 hover:border-primary transition-colors">
          <ShoppingCart className="w-8 h-8 mb-4 text-primary" />
          <h3 className="font-medium mb-1">Manage Orders</h3>
          <p className="text-sm text-muted-foreground">Process orders and update shipping status</p>
        </Link>
        <Link to="/admin/journal" className="p-6 bg-white border border-gray-200 hover:border-primary transition-colors">
          <TrendingUp className="w-8 h-8 mb-4 text-primary" />
          <h3 className="font-medium mb-1">Publish Content</h3>
          <p className="text-sm text-muted-foreground">Write journal posts and update pages</p>
        </Link>
      </div>
    </div>
  );
};

export default AdminDashboard;
